import React, { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp } from 'lucide-react'

/** Höher wächst das Feld nicht — darüber rollt es in sich. */
const MAX_HEIGHT = 160

export interface CommentComposerProps {
  value: string
  onValueChange: (value: string) => void
  /** Schickt den getrimmten Text; nur, wenn er nicht leer ist. */
  onSubmit: (content: string) => void
  /** Escape: die Tastatur zurück an die Liste, der Entwurf bleibt. */
  onReleaseFocus?: () => void
  textareaRef?: React.Ref<HTMLTextAreaElement>
}

/**
 * **Das Eingabefeld unter den Kommentaren** (#183): wächst mit dem Text, Enter schickt,
 * ⇧Enter bricht um, runder Senden-Knopf. Ein Feld mit nur Leerzeichen schickt nichts.
 */
export const CommentComposer: React.FC<CommentComposerProps> = ({ value, onValueChange, onSubmit, onReleaseFocus, textareaRef }) => {
  const { t } = useTranslation()
  const ownRef = useRef<HTMLTextAreaElement | null>(null)
  const content = value.trim()

  // Die Höhe folgt dem Text. Ohne Maß (inaktiver Tab, jsdom) bleibt sie, wie sie ist.
  useLayoutEffect(() => {
    const field = ownRef.current
    if (!field) return
    field.style.height = ''
    if (field.scrollHeight === 0) return
    field.style.height = `${Math.min(field.scrollHeight, MAX_HEIGHT)}px`
  }, [value])

  const submit = () => {
    if (!content) return
    onSubmit(content)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Während einer Eingabe per IME bestätigt Enter das Zeichen, nicht den Kommentar.
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onReleaseFocus?.()
    }
  }

  const setRefs = (node: HTMLTextAreaElement | null) => {
    ownRef.current = node
    if (typeof textareaRef === 'function') textareaRef(node)
    else if (textareaRef) (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
  }

  return (
    <div className="comment-composer">
      <textarea
        ref={setRefs}
        className="comment-composer__field"
        rows={1}
        value={value}
        placeholder={t('taskComments.placeholder')}
        aria-label={t('taskComments.placeholder')}
        onChange={e => onValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        spellCheck={false}
      />
      <button
        type="button"
        className="comment-composer__send"
        aria-label={t('taskComments.send')}
        disabled={!content}
        onClick={submit}
      >
        <ArrowUp size={16} aria-hidden="true" />
      </button>
    </div>
  )
}
